import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as React from 'react';
import '../paging-button/index-chunk.esm.js';
import { c as classNames, l as liveAnnounce, L as Level, d as EGDSContextProvider, u as useEGDSContext } from '../theme-provider/index-chunk.esm.js';
import { a as EGDSPagingPrevButton, E as EGDSPagingNextButton } from '../paging-prev-button/index-chunk.esm.js';
import { debounce } from 'throttle-debounce';
import styles from '../styles/uitk-egds-carousel.css';
import { useLocalization } from '@shared-ui/localization-context';
import { a as analyticsTracker } from '../analytics-tracker/index-chunk.esm.js';
import { M as MakeEgdsSizingLength } from '../egds-sizing-length/index-chunk.esm.js';

const EGDSCarouselBreakPointSizeMap = [
    ['sm', 'sm'],
    ['md', 'md'],
    ['lg', 'lg'],
    ['xl', 'xl'],
];
const EGDSCarouselBreakPointSizes = new Map(EGDSCarouselBreakPointSizeMap);
const EGDSHeadingTagMap = [
    ['h1', 'h1'],
    ['h2', 'h2'],
    ['h3', 'h3'],
    ['h4', 'h4'],
    ['h5', 'h5'],
    ['h6', 'h6'],
];
const EGDSHeadingTags = new Map(EGDSHeadingTagMap);

const getClassNameList$3 = classNames.bind(styles);
var EGDSCarouselLocalAnalyticsIdType;
(function (EGDSCarouselLocalAnalyticsIdType) {
    EGDSCarouselLocalAnalyticsIdType["clickLeft"] = "clickLeft";
    EGDSCarouselLocalAnalyticsIdType["clickRight"] = "clickRight";
    EGDSCarouselLocalAnalyticsIdType["scrollLeft"] = "scrollLeft";
    EGDSCarouselLocalAnalyticsIdType["scrollRight"] = "scrollRight";
})(EGDSCarouselLocalAnalyticsIdType || (EGDSCarouselLocalAnalyticsIdType = {}));
const Carousel = (props) => {
    const { analytics, buttonText, carouselHeaderLevel, carouselHeaderText, carouselHeaderVisible, carouselName, carouselRef, children, className, containerAriaLabel, domRef, fullSheetVisible, hideNav = false, innerSpacing, itemsGap = { sm: 16, md: 16, lg: 16 }, itemsVisible = { sm: 1, md: 2, lg: 3, xl: 4 }, itemsMaxHeight = false, itemType, l10n, naturalWidthItems = false, navigationIconId = {
        next: 'next-button',
        prev: 'prev-button',
    }, onChange, onIndexChange, pagingInset = false, pagingOverlay = false, pagingPersistent, peek = false, peekAmount = { sm: 48, md: 16, lg: 32 }, startIndex = 0, stretchToFill = true, useContainerSizing = true, wrapItems = true, } = props;
    const [mounted, setMounted] = React.useState(false);
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [isTouch, setIsTouch] = React.useState(false);
    const [isRtl, setIsRtl] = React.useState(false);
    const [size, setSize] = React.useState('sm');
    const [itemsGapAmount, setItemsGapAmount] = React.useState(16);
    const [peekWidthAmount, setPeekWidthAmount] = React.useState(48);
    const [innerSpacingAmount, setInnerSpacingAmount] = React.useState(0);
    const [sizeInitialized, setSizeInitialized] = React.useState(false);
    const [isFullyScrolled, setIsFullyScrolled] = React.useState(false);
    const [carouselVisibility, setCarouselVisibility] = React.useState({
        PARTIAL: false,
        FULL: false,
    });
    const [windowWidth, setWindowWidth] = React.useState(0);
    const [scrollEventOngoing, setScrollEventOngoing] = React.useState(false);
    // Below values are reffed because we want them to be maintained between renders
    const NAVIGATE_WITH_BTN = React.useRef(false); // Scrolling using a paging button
    const SCROLL_FROM_RESIZE_EVENT = React.useRef(false); // Scrolling as a result of a resizing event
    const AVOID_FREE_SCROLL = React.useRef(false); // Free scrolling is scrolling the carousel without using the paging buttons
    const CHANGE_INDEX_WITHOUT_ANIMATION = React.useRef(false);
    const SCROLL_WITHOUT_ANIMATION = React.useRef(false);
    const WRAPPER = React.useRef(null);
    const containerRef = React.useRef(null);
    const viewed = React.useRef([]);
    // Distance from origin of the left-most item (or right-most item for RTL)
    const positionWithinCarousel = React.useRef(0);
    // We use this to know if the carousel is going forwards or backwards
    const directionOfScrollIsForward = React.useRef(true);
    // Carousel settings and shorthands
    const INTERACTION_TIMER = 200; // Interaction Timer of resize handler
    const SCROLL_TIMER = 350;
    const NATURAL_WIDTH_ITEMS_GAP_DEFAULT = 12;
    const itemCount = React.Children.count(children);
    const wrapperRef = domRef ?? WRAPPER;
    const currentItemsVisible = itemsVisible[size]; // This can be non-nullable, since setViewportSize will always set a valid size
    const fullImageGalleryType = !naturalWidthItems && currentItemsVisible === 1 && !peek;
    const edgeToEdgeType = peek && peekAmount && innerSpacing;
    const reverseRtlValue = (value) => (isRtl && value !== 0 ? value * -1 : value); // The scrollLeft method returns a negative value in RTL but our functions frequently expect a positive (or reverse) value
    const snapPointsArrayRef = React.useRef([]);
    const initialSnapPointItems = [];
    // This reducer function is fed to carousel items and updated via the UITK Context Provider,
    // in order to determine child offsetLeft amounts and update 'snap points' array for items
    const getSnapPointItemsReducer = (state, action) => {
        const newItem = action.payload;
        const { itemIndex, snapPoint } = newItem;
        const calculateSnapPointFromItemWidth = () => {
            const prevSnapPoint = state.find((stateItem) => stateItem.itemIndex === itemIndex - 1)?.snapPoint;
            if (itemIndex === 0) {
                return innerSpacingAmount || 0;
            }
            return snapPoint + itemsGapAmount + (prevSnapPoint || 0);
        };
        // In RTL mode, we cannot rely on offsetLeft so are relying on the item's width
        const newItemSnapPoint = isRtl ? calculateSnapPointFromItemWidth() : Math.floor(snapPoint);
        const existingIndex = state.findIndex((existingStateItem) => existingStateItem.itemIndex === newItem.itemIndex);
        if (existingIndex !== -1) {
            const updatedState = state.map((stateItem, stateItemIndex) => stateItemIndex === itemIndex ? { ...stateItem, snapPoint: newItemSnapPoint } : stateItem);
            return updatedState;
        }
        newItem.snapPoint = newItemSnapPoint;
        const appendedState = [...state, newItem];
        return appendedState;
    };
    const [snapPointItems, snapPointItemsDispatch] = React.useReducer(getSnapPointItemsReducer, initialSnapPointItems);
    // This reducer function is fed to carousel items and updated via the UITK Context Provider,
    // in order to track which natural width items are currently in view, for liveAnnounce/A11y purposes
    /* istanbul ignore next */
    const [naturalWidthItemsInView, naturalWidthItemsInViewDispatch] = React.useReducer((state, action) => {
        const newItemInView = action.payload;
        const existingIndex = state.findIndex((itemObj) => itemObj.itemIndex === newItemInView.itemIndex);
        if (existingIndex !== -1) {
            const updatedState = state.map((stateItem, stateItemIndex) => stateItemIndex === newItemInView.itemIndex ? { ...stateItem, inView: newItemInView.inView } : stateItem);
            return updatedState.sort((a, b) => a.itemIndex - b.itemIndex);
        }
        const appendedState = [...state, newItemInView];
        return appendedState.sort((a, b) => a.itemIndex - b.itemIndex);
    }, []);
    // This reducer function is fed to carousel items and updated via the UITK Context Provider,
    // in order to track which natural width items have been viewed, for analytics purposes
    /* istanbul ignore next */
    const [naturalWidthItemsViewed, naturalWidthItemsViewedDispatch] = React.useReducer((state, action) => {
        const newItemViewed = action.payload;
        const existingIndex = state.findIndex((itemObj) => itemObj.itemIndex === newItemViewed.itemIndex);
        const newState = existingIndex === -1 ? [...state, newItemViewed] : state;
        return newState.sort((a, b) => a.itemIndex - b.itemIndex);
    }, []);
    const findSnapPointIndex = (target) => {
        // Binary search locates the index of the snap point closest to a target number
        if (snapPointsArrayRef.current.length > 0) {
            const n = snapPointsArrayRef.current.length;
            const snapPointsArray = Object.values(snapPointsArrayRef.current).map((item) => item.snapPoint);
            let left = 0;
            let right = n - 1;
            while (left < right) {
                if (Math.abs(snapPointsArray[left] - target) <= Math.abs(snapPointsArray[right] - target)) {
                    right -= 1;
                }
                else {
                    left += 1;
                }
            }
            return snapPointsArray.indexOf(snapPointsArray[left]);
        }
        return 0;
    };
    // Snap Point generation functions -- Ends
    // A11y related functions -- Starts
    const announceCards = () => {
        if (containerRef.current) {
            const visibleCardHeadingsElements = containerRef.current.querySelectorAll(`
          .uitk-carousel-item:not([aria-hidden="true"]) h1:not([aria-hidden="true"]),
          .uitk-carousel-item:not([aria-hidden="true"]) h2:not([aria-hidden="true"]),
          .uitk-carousel-item:not([aria-hidden="true"]) h3:not([aria-hidden="true"]),
          .uitk-carousel-item:not([aria-hidden="true"]) h4:not([aria-hidden="true"]),
          .uitk-carousel-item:not([aria-hidden="true"]) h5:not([aria-hidden="true"]),
          .uitk-carousel-item:not([aria-hidden="true"]) h6:not([aria-hidden="true"])
        `);
            const visibleCardHeadings = Array.from(visibleCardHeadingsElements).map((heading) => heading.innerText || heading.textContent);
            if (naturalWidthItems) {
                // Grab the indices of all carousel items that are visible to the user.
                const naturalWidthItemsCurrentlyVisibleIndices = naturalWidthItemsInView
                    .filter((item) => item.inView)
                    .map((item) => item.itemIndex);
                // Grab the content of all carousel items and store in an array.
                const naturalWidthElements = containerRef.current.querySelectorAll(`
          .uitk-carousel-item
        `);
                const naturalWidthElementsContent = Array.from(naturalWidthElements).map((e) => e.innerText || e.textContent);
                // Filter the content array so that only the content of visible carousel items is stored.
                const visibleNaturalWidthSpans = naturalWidthElementsContent.filter((_, i) => naturalWidthItemsCurrentlyVisibleIndices.includes(i));
                setTimeout(() => {
                    liveAnnounce(l10n.formatText('carousel.cardChange', visibleNaturalWidthSpans.join(', ')), Level.POLITE);
                }, 200);
            }
            else if (itemType) {
                // Get the liveannounce reading for each card
                const cardLabels = [...Array(currentItemsVisible)].map((_, i) => `${itemType} ${l10n?.formatText('carousel.indexAndTotal', currentIndex + i + 1, itemCount)}`);
                setTimeout(() => {
                    liveAnnounce(l10n.formatText('carousel.cardChange', cardLabels.join(', ')), Level.POLITE);
                }, 200);
            }
            else if (visibleCardHeadings.length > 0) {
                setTimeout(() => {
                    liveAnnounce(l10n.formatText('carousel.cardChange', visibleCardHeadings.join(', ')), Level.POLITE);
                }, 200);
            }
            else {
                setTimeout(() => {
                    liveAnnounce(l10n.formatText('gallerycarousel.imageChange', currentIndex + 1, itemCount, ''), Level.POLITE);
                }, 200);
            }
        }
    };
    // A11y related functions -- Ends
    // Analytics functions -- Starts
    const viewItemAnalytics = () => {
        const setAnalyticsTrackingValues = (itemIndex) => {
            if (!viewed.current[itemIndex]) {
                viewed.current[itemIndex] = true;
                if (typeof props.itemViewedCallback === 'function') {
                    props.itemViewedCallback(itemIndex);
                }
                analytics && analyticsTracker(analytics, `${carouselName}.Carousel.itemViewed.${itemIndex}`, '');
            }
        };
        if (naturalWidthItems) {
            naturalWidthItemsViewed.forEach((itemViewed) => {
                setAnalyticsTrackingValues(itemViewed.itemIndex);
            });
        }
        else {
            let vis = currentItemsVisible;
            if (peek) {
                vis += 1;
            }
            for (let i = 0; i < vis && currentIndex + i < React.Children.count(children); ++i) {
                setAnalyticsTrackingValues(currentIndex + i);
            }
        }
    };
    const scrollAnalytics = React.useCallback((scrollRight) => {
        analytics &&
            analyticsTracker(analytics, `${carouselName}.Carousel.${scrollRight ? EGDSCarouselLocalAnalyticsIdType.scrollRight : EGDSCarouselLocalAnalyticsIdType.scrollLeft}`, '');
    }, [analytics, carouselName]);
    // Analytics functions -- Ends
    // Carousel main functions -- Starts
    const getCurrentIndex = (currentPosition) => {
        if (containerRef.current) {
            // FullImageGalleryType does not have the snap points array populated during scroll (unknown why),
            // so we need to rely on a manual calculation in this case
            if (fullImageGalleryType) {
                const carouselWidth = containerRef.current.getBoundingClientRect().width;
                const distanceFromOrigin = Math.abs(currentPosition);
                const calculatedCurrentIndex = Math.round(distanceFromOrigin / carouselWidth);
                return calculatedCurrentIndex;
            }
            return findSnapPointIndex(currentPosition);
        }
        return 0;
    };
    const getCurrentIndexForNaturalWidthItems = (currentPosition) => {
        if (containerRef.current) {
            const targetIndex = findSnapPointIndex(currentPosition);
            return targetIndex;
        }
        return 0;
    };
    const checkIsFullyScrolled = () => {
        const containerElement = containerRef.current;
        if (containerElement) {
            const { scrollLeft, clientWidth, scrollWidth } = containerElement;
            const maxIncrementPoint = scrollWidth - clientWidth - 1;
            setIsFullyScrolled(reverseRtlValue(scrollLeft) >= maxIncrementPoint);
        }
    };
    /* istanbul ignore next */
    const getIncrementalPosition = (time, positionToSnapTo, positionUserScrolledTo, range) => {
        // Quadratic easein-easeout function // refer to https://easings.net/#easeInOutQuad
        const curve = (x) => (x < 0.5 ? 2 * x ** 2 : 1 - (-2 * x + 2) ** 2 / 2);
        const done = time >= SCROLL_TIMER || SCROLL_WITHOUT_ANIMATION.current;
        const position = done ? positionToSnapTo : positionUserScrolledTo + range * curve(time / SCROLL_TIMER);
        return { done, position };
    };
    // Scroll carousel to newPosition pixels from origin (which is the left edge for LTR and the right edge for RTL)
    const scrollCarousel = (newPosition) => {
        let startTimestamp;
        if (containerRef.current) {
            !CHANGE_INDEX_WITHOUT_ANIMATION.current && setScrollEventOngoing(true);
            const containerElement = containerRef.current;
            const positionUserCurrentlyScrolledTo = reverseRtlValue(containerElement.scrollLeft);
            const range = newPosition - positionUserCurrentlyScrolledTo;
            // Incrementally advance the carousel using the curve value described above
            // istanbul ignore next
            const step = (timestamp) => {
                if (!startTimestamp) {
                    startTimestamp = timestamp;
                }
                const { done, position } = getIncrementalPosition(timestamp - startTimestamp, newPosition, positionUserCurrentlyScrolledTo, range);
                containerElement.scrollLeft = fullImageGalleryType ? reverseRtlValue(newPosition) : reverseRtlValue(position); // This moves the carousel
                if (done || fullImageGalleryType) {
                    checkIsFullyScrolled();
                    setScrollEventOngoing(false);
                    !SCROLL_FROM_RESIZE_EVENT.current && scrollAnalytics(range > 0);
                }
                else {
                    window.requestAnimationFrame(step);
                }
            };
            if (CHANGE_INDEX_WITHOUT_ANIMATION.current) {
                // Occurs when startIndex is set
                containerElement.scrollLeft = reverseRtlValue(newPosition);
                checkIsFullyScrolled();
                setMounted(true);
                CHANGE_INDEX_WITHOUT_ANIMATION.current = false;
            }
            else {
                window.requestAnimationFrame(step);
            }
            positionWithinCarousel.current = newPosition;
        }
    };
    const incrementIndexAsNeeded = (index) => {
        /* istanbul ignore else */
        if (directionOfScrollIsForward.current === true) {
            return index + 1;
        }
        return index;
    };
    const setScrollingForwardRef = (position) => {
        if (position > positionWithinCarousel.current) {
            directionOfScrollIsForward.current = true;
        }
        else {
            directionOfScrollIsForward.current = false;
        }
    };
    const getIndexToSet = (index) => {
        const maxIndex = itemCount - currentItemsVisible;
        const newIndex = incrementIndexAsNeeded(index);
        if (newIndex > maxIndex) {
            return maxIndex;
        }
        return newIndex;
    };
    // Adding this ignore comment to prevent this issue https://jira.expedia.biz/browse/EGDS-3032
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onScroll = React.useCallback(debounce(INTERACTION_TIMER, () => {
        if (AVOID_FREE_SCROLL.current) {
            AVOID_FREE_SCROLL.current = false;
            return;
        }
        if (NAVIGATE_WITH_BTN.current) {
            NAVIGATE_WITH_BTN.current = false;
            return;
        }
        CHANGE_INDEX_WITHOUT_ANIMATION.current = false;
        if (containerRef.current) {
            const positionUserScrolledTo = reverseRtlValue(containerRef.current.scrollLeft);
            setScrollingForwardRef(positionUserScrolledTo);
            const isRightScroll = positionUserScrolledTo - positionWithinCarousel.current > 0;
            scrollAnalytics(isRightScroll);
            positionWithinCarousel.current = positionUserScrolledTo;
            if (SCROLL_FROM_RESIZE_EVENT.current) {
                SCROLL_FROM_RESIZE_EVENT.current = false;
            }
            else {
                const currentIndexAfterScroll = naturalWidthItems
                    ? getCurrentIndexForNaturalWidthItems(positionUserScrolledTo)
                    : getCurrentIndex(positionUserScrolledTo);
                const indexToSet = fullImageGalleryType ? currentIndexAfterScroll : getIndexToSet(currentIndexAfterScroll);
                setCurrentIndex(indexToSet);
            }
            checkIsFullyScrolled();
        }
    }), [size, isRtl]);
    // Prevents tab events from activating scrolling
    const onKeyboardEvent = React.useCallback((e) => {
        if (e.key) {
            NAVIGATE_WITH_BTN.current = true;
            AVOID_FREE_SCROLL.current = false;
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [size, isRtl]);
    React.useEffect(() => {
        document.body.addEventListener('keydown', onKeyboardEvent);
        return () => {
            document.body.removeEventListener('keydown', onKeyboardEvent);
        };
    }, [onKeyboardEvent]);
    const getScrollPointForIndex = (index) => {
        if (!peek || edgeToEdgeType) {
            return snapPointItems[index] && snapPointItems[index].snapPoint - innerSpacingAmount;
        }
        if (peek && !edgeToEdgeType) {
            const snapPointAdjustment = currentItemsVisible > 1 ? (peekWidthAmount + itemsGapAmount) / 2 - innerSpacingAmount : 0;
            return snapPointItems[index] && snapPointItems[index].snapPoint - snapPointAdjustment;
        }
    };
    const checkNavigationShouldBeVisible = () => {
        if (naturalWidthItems) {
            const containerElement = containerRef?.current;
            if (containerElement) {
                const carouselWidth = containerElement.getBoundingClientRect().width;
                const { scrollWidth } = containerElement;
                return Math.ceil(carouselWidth) < scrollWidth;
            }
        }
        else {
            return itemCount > currentItemsVisible;
        }
    };
    const adjustLayoutSettingsByCarouselSize = (carouselSize) => {
        // Override prop setting for natural width items
        const itemsGapLookup = naturalWidthItems
            ? NATURAL_WIDTH_ITEMS_GAP_DEFAULT
            : itemsGap && itemsGap[carouselSize];
        const innerSpacingAmountLookup = innerSpacing && innerSpacing[carouselSize];
        const peekWidthAmountLookup = peek && peekAmount && peekAmount[carouselSize];
        // If innerSpacing is set, add it to peekWidthAmount
        const calculatedPeekWidthAmount = innerSpacingAmountLookup
            ? peekWidthAmountLookup + innerSpacingAmountLookup
            : peekWidthAmountLookup;
        itemsGapLookup && setItemsGapAmount(Number(MakeEgdsSizingLength(itemsGapLookup, false)));
        calculatedPeekWidthAmount && setPeekWidthAmount(Number(MakeEgdsSizingLength(calculatedPeekWidthAmount, false)));
        innerSpacingAmountLookup && setInnerSpacingAmount(Number(MakeEgdsSizingLength(innerSpacingAmountLookup, false)));
    };
    const setViewportSize = () => {
        const containerElement = containerRef?.current;
        window && setWindowWidth(window.innerWidth);
        if (containerElement) {
            let carouselSize = getComputedStyle(containerElement).getPropertyValue('--size') || 'sm';
            if (carouselSize === 'xl' && !itemsVisible.xl) {
                carouselSize = 'lg'; // Backward compatibility if xl is not passed
            }
            if (carouselSize) {
                adjustLayoutSettingsByCarouselSize(carouselSize);
            }
            setSize(carouselSize);
            if (!sizeInitialized)
                setSizeInitialized(true);
        }
    };
    const page = (direction) => {
        SCROLL_FROM_RESIZE_EVENT.current = false;
        CHANGE_INDEX_WITHOUT_ANIMATION.current = false;
        if (naturalWidthItems) {
            const containerElement = containerRef.current;
            if (containerElement) {
                const carouselWidth = containerElement.getBoundingClientRect().width;
                const pagingDistance = carouselWidth * 0.75;
                const currentPosition = reverseRtlValue(containerElement.scrollLeft);
                const { clientWidth, scrollWidth } = containerElement;
                const maxIncrementPoint = scrollWidth - clientWidth - innerSpacingAmount * 2;
                const targetPosition = direction === 1 ? currentPosition + pagingDistance : currentPosition - pagingDistance;
                const isAboutToHitMax = direction === 1 && targetPosition > maxIncrementPoint;
                const targetIndex = findSnapPointIndex(targetPosition);
                const maxIndex = findSnapPointIndex(maxIncrementPoint) + 1;
                if (isAboutToHitMax) {
                    setCurrentIndex(maxIndex);
                    // Solves for edge case when carousel is scrolled 'nearly' to the end;
                    // bypass typical paging behavior and scroll to the end
                    !isFullyScrolled && direction === 1 && scrollCarousel(snapPointItems[maxIndex]?.snapPoint);
                }
                else {
                    setCurrentIndex(targetIndex);
                }
            }
        }
        else {
            const capPageBy = currentItemsVisible;
            let index = direction === 1 ? currentIndex + Number(capPageBy) : currentIndex - Number(capPageBy);
            const limit = itemCount - currentItemsVisible;
            if (index < 0) {
                index = 0;
            }
            else if (index >= limit) {
                index = limit;
            }
            setCurrentIndex(index);
            // Solves for edge case when carousel is scrolled 'nearly' to the end;
            // bypass typical paging behavior and scroll to the end
            if (index === limit && !isFullyScrolled && direction === 1) {
                scrollCarousel(snapPointItems[index]?.snapPoint);
            }
        }
        NAVIGATE_WITH_BTN.current = true;
    };
    const onPrevCb = () => {
        page(-1);
    };
    const onNextCb = () => {
        page(1);
    };
    // Changes the carousel's current index
    const changeIndex = (index) => {
        // This is set to bypass scroll event callbacks
        NAVIGATE_WITH_BTN.current = true;
        let newIndex;
        let maxIndex;
        if (naturalWidthItems && containerRef.current) {
            const { clientWidth, scrollWidth } = containerRef.current;
            const maxIncrementPoint = scrollWidth - clientWidth - innerSpacingAmount * 2;
            maxIndex = findSnapPointIndex(maxIncrementPoint) + 1;
        }
        else {
            maxIndex = itemCount - currentItemsVisible;
        }
        if (index < 0) {
            newIndex = 0;
        }
        else if (index >= maxIndex) {
            newIndex = maxIndex;
        }
        else {
            newIndex = index;
        }
        setCurrentIndex(newIndex);
    };
    // Hooks
    React.useEffect(() => {
        mounted && announceCards();
        if (sizeInitialized && carouselVisibility.FULL)
            viewItemAnalytics();
        // Turn off free scroll and yet advance the carousel
        if (NAVIGATE_WITH_BTN.current) {
            SCROLL_FROM_RESIZE_EVENT.current = false;
            NAVIGATE_WITH_BTN.current = false;
            AVOID_FREE_SCROLL.current = true;
            const scrollPoint = getScrollPointForIndex(currentIndex);
            scrollCarousel(scrollPoint);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carouselVisibility.FULL, currentIndex, naturalWidthItemsInView, naturalWidthItemsViewed, sizeInitialized]);
    React.useEffect(() => {
        onIndexChange && onIndexChange(currentIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex]);
    React.useEffect(() => {
        // Ref the snap points of the carousel so that they don't get blasted away on scroll, as can happen with state
        snapPointsArrayRef.current = snapPointItems;
    }, [snapPointItems]);
    React.useEffect(() => {
        // Initialize viewport size
        setViewportSize();
        // Ensure that scrollLeft position is reset on mount
        if (!startIndex && containerRef.current) {
            containerRef.current.scrollLeft = 0;
        }
        // Change carousel's index to startIndex without triggering scroll animation
        if (startIndex) {
            CHANGE_INDEX_WITHOUT_ANIMATION.current = true;
            changeIndex(startIndex);
        }
        else {
            setMounted(true);
        }
        'ontouchstart' in window && setIsTouch(true);
        document.querySelector('html').getAttribute('dir') === 'rtl' && setIsRtl(true);
        // Adds empty aria-live region to page to ensure Jaws screen reader
        // announces on initial key press. Relates to https://jira.expedia.biz/browse/WCAG-8258
        liveAnnounce('');
        if (sizeInitialized && carouselVisibility.FULL)
            viewItemAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sizeInitialized]);
    React.useEffect(() => {
        window && setWindowWidth(window.innerWidth);
        const debounceResizeHandler = debounce(INTERACTION_TIMER, () => {
            if (windowWidth === window.innerWidth) {
                return;
            }
            setWindowWidth(window.innerWidth);
            setViewportSize();
        });
        window.addEventListener('resize', debounceResizeHandler);
        return () => window.removeEventListener('resize', debounceResizeHandler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [size, currentIndex]);
    React.useEffect(() => {
        const mediaQuery = window && window.matchMedia('(prefers-reduced-motion: reduce)');
        SCROLL_WITHOUT_ANIMATION.current = !mediaQuery || mediaQuery.matches;
        mediaQuery.addEventListener('change', () => {
            SCROLL_WITHOUT_ANIMATION.current = mediaQuery.matches;
        });
    }, []);
    React.useEffect(() => {
        // Ensures that upon resize, the carousel will scroll to a position where the updated index slide will completely show.
        // Natural width item-type carousels do not need this, since their dimensions don't change on resize
        if (!naturalWidthItems && !CHANGE_INDEX_WITHOUT_ANIMATION.current) {
            const currentPosition = reverseRtlValue(containerRef?.current?.scrollLeft);
            const updatedCurrentIndexOnResize = getCurrentIndex(currentPosition);
            /* istanbul ignore next */
            if (updatedCurrentIndexOnResize > 0 && snapPointItems) {
                SCROLL_FROM_RESIZE_EVENT.current = true;
                const scrollPointAtNewIndex = getScrollPointForIndex(updatedCurrentIndexOnResize);
                if (scrollPointAtNewIndex) {
                    setCurrentIndex(updatedCurrentIndexOnResize);
                    scrollCarousel(scrollPointAtNewIndex);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [size, windowWidth, snapPointItems]);
    React.useEffect(() => {
        onChange && onChange(containerRef);
    });
    React.useEffect(() => {
        // Used to determine if carousel is fully visible and trigger analytics for visible items accordingly
        const intersectionObserverOptions = {
            threshold: [0, 1],
            root: null,
        };
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                setCarouselVisibility({
                    PARTIAL: entry.isIntersecting,
                    FULL: entry.intersectionRatio === 1,
                });
            });
        }, intersectionObserverOptions);
        const containerRefCurrent = containerRef.current;
        containerRefCurrent && observer.observe(containerRefCurrent);
        return () => {
            containerRefCurrent && observer.unobserve(containerRefCurrent);
        };
    }, []);
    const contextData = React.useMemo(() => ({
        isNaturalWidthItemsCarousel: naturalWidthItems,
        carouselWindowWidth: windowWidth,
        carouselSize: size,
        setSnapPointItem: snapPointItemsDispatch,
        setNaturalWidthItemsInView: naturalWidthItemsInViewDispatch,
        setNaturalWidthItemsViewed: naturalWidthItemsViewedDispatch,
        isRtl,
        carouselIsVisible: carouselVisibility.PARTIAL,
    }), [naturalWidthItems, windowWidth, isRtl, size, carouselVisibility.PARTIAL]);
    const roundedItemsGap = React.useMemo(() => MakeEgdsSizingLength(itemsGap[size]), [size, itemsGap]);
    const fullPeekAmountObj = React.useMemo(() => ({
        sm: peekAmount.sm + (innerSpacing?.sm || 0),
        md: peekAmount.md + (innerSpacing?.md || 0),
        lg: peekAmount.lg + (innerSpacing?.lg || 0),
        xl: peekAmount.xl ? peekAmount.xl + (innerSpacing?.xl || 0) : peekAmount.lg + (innerSpacing?.lg || 0),
    }), [peekAmount, innerSpacing]);
    const getCssCustomPropertiesForBreakpointBasedProp = React.useCallback((customPropExcerpt, breakPointValuesObj, returnRoundedPxValue = true) => {
        const customPropsObj = {};
        Object.keys(breakPointValuesObj).forEach((breakpoint) => {
            const value = breakPointValuesObj[breakpoint];
            const formattedValue = returnRoundedPxValue ? `${MakeEgdsSizingLength(value)}` : value;
            customPropsObj[`--uitk-carousel-${customPropExcerpt}-${breakpoint}`] = formattedValue;
        });
        return customPropsObj;
    }, []);
    const getGapCssCustomProp = React.useCallback(() => {
        if (fullImageGalleryType) {
            return '0px';
        }
        if (naturalWidthItems) {
            return `${NATURAL_WIDTH_ITEMS_GAP_DEFAULT}px`;
        }
        return roundedItemsGap;
    }, [roundedItemsGap, fullImageGalleryType, naturalWidthItems]);
    const renderCarousel = () => {
        const cssClassNames = getClassNameList$3('uitk-carousel', (!peek || !(currentItemsVisible < itemCount)) && 'no-peek', !pagingInset && 'no-inset', !isTouch && 'no-touch', itemsMaxHeight && 'items-max-height', fullImageGalleryType && 'full-image-gallery-type', innerSpacing && 'has-inner-spacing', pagingPersistent && 'has-persistent-nav', className, !!size && typeof window !== 'undefined' ? size : 'sm');
        const carouselContainerClassNames = getClassNameList$3('uitk-carousel-container', fullImageGalleryType && 'full-image-gallery-type', (SCROLL_WITHOUT_ANIMATION.current || CHANGE_INDEX_WITHOUT_ANIMATION.current) && 'no-animation-on-scroll', naturalWidthItems && 'has-natural-width-items', useContainerSizing && 'use-container-sizing');
        const navProps = {
            analytics,
            buttonText,
            carouselName,
            trackingIndex: currentIndex,
            itemCount,
            itemsVisible,
            fullSheetVisible,
            navigationIconId,
            onNextCb,
            onPrevCb,
            pagingOverlay,
            scrollEventOngoing,
            size,
            isRtl,
            naturalWidthItems,
            carouselContainerRef: containerRef,
            showPrevNavButton: currentIndex > 0,
            showNextNavButton: fullImageGalleryType ? currentIndex < itemCount - 1 : !isFullyScrolled,
        };
        const CarouselHeaderLevel = ((carouselHeaderLevel && EGDSHeadingTags.get(carouselHeaderLevel)) ||
            EGDSHeadingTags.get('h3'));
        const shouldRenderNav = checkNavigationShouldBeVisible();
        // A custom prop for each size value (sm,md,etc) -- this is verbose, and these could be instead dynamically changed with JS;
        // but doing it the verbose way ensures that the carousel will always have a value for each breakpoint without hydration,
        // and removes the potential for a layout shift that otherwise can occur prior to full load/hydration
        const cssCustomProperties = {
            '--uitk-carousel-items-gap': getGapCssCustomProp(),
            ...(innerSpacing && {
                '--uitk-carousel-inner-spacing': `${innerSpacingAmount}px`,
                ...getCssCustomPropertiesForBreakpointBasedProp('inner-spacing', innerSpacing),
            }),
            ...(!fullImageGalleryType &&
                !naturalWidthItems && {
                ...getCssCustomPropertiesForBreakpointBasedProp('items-gap', itemsGap),
                ...getCssCustomPropertiesForBreakpointBasedProp('peek-amount', fullPeekAmountObj),
                ...getCssCustomPropertiesForBreakpointBasedProp('item-count', itemsVisible, false),
            }),
        };
        return (jsx(EGDSContextProvider, { config: { context: 'uitk-carousel-context', data: contextData }, children: jsxs("div", { className: cssClassNames, ref: wrapperRef, "data-stid": "carousel-wrapper", children: [carouselHeaderText && (jsx(CarouselHeaderLevel, { className: getClassNameList$3({ [`is-visually-hidden`]: !carouselHeaderVisible }), children: carouselHeaderText })), jsx("div", { className: carouselContainerClassNames, "aria-label": containerAriaLabel ?? carouselHeaderText, ref: containerRef, onScroll: onScroll, "data-stid": "carousel-container", style: cssCustomProperties, tabIndex: 0, children: jsx(EGDSCarouselItemList, { itemsVisible: itemsVisible, size: size, currentIndex: currentIndex, l10n: l10n, peek: peek, wrapItems: naturalWidthItems || fullImageGalleryType ? true : wrapItems, itemCount: itemCount, stretchToFill: stretchToFill, children: children }) }), !hideNav && shouldRenderNav && jsx(EGDSCarouselNav, { ...navProps })] }) }));
    };
    React.useImperativeHandle(carouselRef, () => ({
        changeIndex: (index) => changeIndex(index),
    }));
    // Carousel main functions -- Ends
    return renderCarousel();
};
const EGDSCarousel = (props) => {
    const l10n = useLocalization();
    return jsx(Carousel, { l10n: l10n, ...props });
};
EGDSCarousel.displayName = 'EGDSCarousel';

const getClassNameList$2 = classNames.bind(styles);
const EGDSCarouselItem = (props) => {
    const { children, wrapItems, ariaHidden, ariaLabel, stretchToFill, carouselContainerRef, itemIndex } = props;
    const [isNaturalWidthItemVisible, setIsNaturalWidthItemVisible] = React.useState(false);
    const itemRef = React.useRef(null);
    const { data: contextData } = useEGDSContext();
    const { carouselWindowWidth, carouselSize, isNaturalWidthItemsCarousel, setSnapPointItem, setNaturalWidthItemsViewed, setNaturalWidthItemsInView, isRtl, carouselIsVisible, } = contextData ?? {};
    let baseClassName = 'uitk-carousel-item';
    if (!stretchToFill) {
        baseClassName += ' uitk-carousel-item-size-auto';
    }
    const toggleAriaHidden = (item) => {
        const itemClassName = wrapItems ? item.props.className : '';
        const a11yHiddenAttrs = {
            'aria-hidden': true,
            tabIndex: -1,
        };
        const hideItemFromScreenReaders = (!isNaturalWidthItemsCarousel && ariaHidden) || (isNaturalWidthItemsCarousel && !isNaturalWidthItemVisible);
        const itemProps = {
            ...(hideItemFromScreenReaders ? a11yHiddenAttrs : null),
            className: getClassNameList$2(itemClassName ? `${itemClassName} ${baseClassName}` : baseClassName),
            'data-stid': 'carousel-item',
            'aria-label': ariaLabel,
            ref: itemRef,
        };
        // Hides inner focusable nodes of a carousel item from screen readers when out of view
        if (itemRef.current) {
            const allMatchingInnerNodes = itemRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
            const arrayOfMatchingInnerNodes = Array.from(allMatchingInnerNodes);
            arrayOfMatchingInnerNodes
                .filter((elem) => !elem.hasAttribute('aria-hidden'))
                .forEach((matchingNode) => {
                matchingNode[hideItemFromScreenReaders ? 'setAttribute' : 'removeAttribute']('tabindex', '-1');
            });
        }
        if (!wrapItems) {
            return React.cloneElement(item, itemProps);
        }
        return jsx("div", { ...itemProps, children: item });
    };
    const useEnhancedEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;
    useEnhancedEffect(() => {
        if (isRtl) {
            // We can't rely on the offsetLeft value for RTL carousel, so are using the item's width instead
            const itemBoundingClientWidth = itemRef.current?.getBoundingClientRect().width;
            if (itemIndex !== undefined && itemBoundingClientWidth) {
                setSnapPointItem && setSnapPointItem({ payload: { itemIndex, snapPoint: itemBoundingClientWidth } });
            }
        }
        else {
            const itemOffsetLeft = itemRef.current?.offsetLeft;
            setSnapPointItem && setSnapPointItem({ payload: { itemIndex, snapPoint: itemOffsetLeft } });
        }
    }, [carouselWindowWidth, carouselIsVisible, carouselSize, isRtl]);
    React.useEffect(() => {
        // Used to determine if the natural width item is visible and set a11y-related attributes
        const intersectionObserverOptions = {
            threshold: [0, 0.25, 0.5, 0.75, 1],
            root: carouselContainerRef?.current,
        };
        /* istanbul ignore next */
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const isFullyVisible = entry.intersectionRatio === 1;
                setIsNaturalWidthItemVisible(isFullyVisible);
                setNaturalWidthItemsInView({ payload: { itemIndex, inView: isFullyVisible } });
                isFullyVisible && setNaturalWidthItemsViewed({ payload: { itemIndex } });
            });
        }, intersectionObserverOptions);
        const itemRefCurrent = itemRef.current;
        isNaturalWidthItemsCarousel && itemRefCurrent && observer.observe(itemRefCurrent);
        return () => {
            itemRefCurrent && observer.unobserve(itemRefCurrent);
        };
    }, [
        carouselContainerRef,
        isNaturalWidthItemsCarousel,
        itemIndex,
        setNaturalWidthItemsViewed,
        setNaturalWidthItemsInView,
    ]);
    const renderCarouselItem = () => {
        const item = children;
        return toggleAriaHidden(item);
    };
    return renderCarouselItem();
};
EGDSCarouselItem.displayName = 'EGDSCarouselItem';
EGDSCarouselItem.defaultProps = {
    wrapItems: true,
};

const EGDSCarouselItemList = (props) => {
    const { children, l10n, size, currentIndex, itemCount, ...itemProps } = props;
    const { itemsVisible } = itemProps;
    const newChildren = React.Children.toArray(children);
    const getCarouselItems = () => {
        const items = newChildren;
        const currentItemsVisible = itemsVisible[size];
        // Determines the grouping to apply aria-hidden to.
        const startOfCurrentGroup = currentIndex;
        const startOfNextGroup = currentIndex + currentItemsVisible;
        const elements = React.Children.map(items, (elm, i) => {
            const ariaHidden = i < startOfCurrentGroup || i >= startOfNextGroup;
            const ariaLabel = l10n?.formatText('carousel.indexAndTotal', i + 1, itemCount);
            return (jsx(EGDSCarouselItem, { itemIndex: i, ariaHidden: ariaHidden, ariaLabel: ariaLabel, ...itemProps, children: elm }, i));
        });
        return elements;
    };
    return jsx(Fragment, { children: getCarouselItems() });
};
EGDSCarouselItemList.displayName = 'EGDSCarouselItemList';

const getClassNameList$1 = classNames.bind(styles);
const EGDSCarouselNav = (props) => {
    const { analytics, buttonText, carouselName, naturalWidthItems, trackingIndex, carouselContainerRef, itemCount, itemsVisible, fullSheetVisible, navigationIconId, onNextCb, onPrevCb, pagingOverlay, scrollEventOngoing = false, size, isRtl, showPrevNavButton = false, showNextNavButton = true, } = props;
    const currentItemsVisible = itemsVisible[size];
    const baseButtonClass = 'uitk-carousel-button-paging';
    const prevBtnClassNames = getClassNameList$1(baseButtonClass, `${baseButtonClass}-prev`, !showPrevNavButton && `${baseButtonClass}-hidden`);
    const nextBtnClassNames = getClassNameList$1(baseButtonClass, `${baseButtonClass}-next`, !showNextNavButton && `${baseButtonClass}-hidden`);
    const nextBtnRef = React.useRef(null);
    const prevBtnRef = React.useRef(null);
    // Fixes issue on mobile devices where touch events trigger paging events multiple times
    const DEBOUNCE_TIMER = typeof window !== 'undefined' && 'ontouchstart' in window ? 50 : 0;
    const clickLeftAnalytics = analytics && {
        localAnalyticsId: `${carouselName}.Carousel.${EGDSCarouselLocalAnalyticsIdType.clickLeft}`,
        ...analytics,
    };
    const clickRightAnalytics = analytics && {
        localAnalyticsId: `${carouselName}.Carousel.${EGDSCarouselLocalAnalyticsIdType.clickRight}`,
        ...analytics,
    };
    const focusNavigationBtn = (ref) => {
        /* istanbul ignore else */
        if (ref && ref.current) {
            ref.current.focus();
        }
    };
    const onPrev = debounce(DEBOUNCE_TIMER, () => {
        /* istanbul ignore else */
        onPrevCb && onPrevCb();
        if (naturalWidthItems && carouselContainerRef?.current) {
            const { scrollLeft: currentPosition } = carouselContainerRef.current;
            const pagingDistance = carouselContainerRef.current.getBoundingClientRect().width * 0.75;
            const targetPosition = currentPosition - pagingDistance;
            if (targetPosition <= 0) {
                focusNavigationBtn(nextBtnRef);
            }
        }
        else if (currentItemsVisible > 0 && trackingIndex <= currentItemsVisible) {
            focusNavigationBtn(nextBtnRef);
        }
    });
    const onNext = debounce(DEBOUNCE_TIMER, () => {
        /* istanbul ignore else */
        onNextCb && onNextCb();
        const trueCurrentIndex = trackingIndex + currentItemsVisible;
        if (naturalWidthItems && carouselContainerRef?.current) {
            const { scrollLeft: currentPosition, scrollWidth, clientWidth } = carouselContainerRef.current;
            const pagingDistance = carouselContainerRef.current.getBoundingClientRect().width * 0.75;
            const targetPosition = currentPosition + pagingDistance;
            const maxIncrementPoint = scrollWidth - clientWidth;
            if (targetPosition >= maxIncrementPoint) {
                focusNavigationBtn(prevBtnRef);
            }
        }
        else if (currentItemsVisible > 0 && trueCurrentIndex >= itemCount - currentItemsVisible) {
            focusNavigationBtn(prevBtnRef);
        }
    });
    const onKeyboardEvent = React.useCallback((e) => {
        /* istanbul ignore else */
        if (isRtl) {
            /* istanbul ignore else */
            if (e.key === 'ArrowLeft') {
                onNext();
            }
            else if (e.key === 'ArrowRight') {
                onPrev();
            }
        }
        else if (e.key === 'ArrowLeft') {
            onPrev();
        }
        else if (e.key === 'ArrowRight') {
            onNext();
        }
    }, 
    // Linter states that onNext & onPrev should be dependencies here (eslint-plugin-react-hooks/exhaustive-deps)
    [isRtl, onNext, onPrev]);
    const renderNav = () => {
        const controlsContainerCssClassNames = getClassNameList$1('uitk-carousel-controls');
        return (jsxs("div", { className: controlsContainerCssClassNames, children: [jsx(EGDSPagingPrevButton, { analytics: clickLeftAnalytics, className: prevBtnClassNames, iconId: navigationIconId?.prev, iconTitle: buttonText.prevButton, onClick: scrollEventOngoing && naturalWidthItems ? undefined : onPrev, domRef: prevBtnRef, "data-stid": "carousel-nav-prev", tabIndex: showPrevNavButton ? 0 : -1, "aria-hidden": !showPrevNavButton, "aria-label": buttonText.prevButton, pagingOverlay: pagingOverlay }), jsx(EGDSPagingNextButton, { analytics: clickRightAnalytics, className: nextBtnClassNames, iconId: navigationIconId?.next, iconTitle: buttonText.nextButton, onClick: scrollEventOngoing && naturalWidthItems ? undefined : onNext, domRef: nextBtnRef, "data-stid": "carousel-nav-next", "aria-label": buttonText.nextButton, "aria-hidden": !showNextNavButton, tabIndex: showNextNavButton ? 0 : -1, pagingOverlay: pagingOverlay })] }));
    };
    React.useEffect(() => {
        if (fullSheetVisible) {
            document.body.addEventListener('keydown', onKeyboardEvent);
            return () => {
                document.body.removeEventListener('keydown', onKeyboardEvent);
            };
        }
        // Linter states that onKeyboardEvent should be a dependency here (eslint-plugin-react-hooks/exhaustive-deps)
    }, [fullSheetVisible, onKeyboardEvent]);
    return renderNav();
};
EGDSCarouselNav.displayName = 'EGDSCarouselNav';

const getClassNameList = classNames.bind(styles);
const CarouselPaginationDots = (props) => {
    const { activeIndex, changeIndex, inset, itemCount, l10n } = props;
    const containerRef = React.createRef();
    const { formatText } = l10n;
    // For now setting maximum pagination dots to 5, but this could be made a prop
    const maxDots = 5;
    const baseClassName = 'uitk-carousel-pagination-dots';
    const isAtBeginning = (currentIndex = activeIndex) => currentIndex <= Math.floor(maxDots / 2);
    const isAtEnd = (currentIndex = activeIndex) => currentIndex >= itemCount - 1 - Math.floor(maxDots / 2);
    const calculateStartIndex = (newActiveIndex) => {
        let newStartIndex;
        if (isAtBeginning(newActiveIndex)) {
            newStartIndex = 0;
        }
        else if (isAtEnd(newActiveIndex)) {
            newStartIndex = itemCount - maxDots;
        }
        else {
            newStartIndex = activeIndex - Math.floor(maxDots / 2);
        }
        return newStartIndex;
    };
    const [startIndex, setStartIndex] = React.useState(calculateStartIndex);
    // Update starting index whenever active index changes
    React.useEffect(() => {
        setStartIndex(calculateStartIndex());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex]);
    // When updating the start index, scroll horizontally until the start index button is visible
    React.useEffect(() => {
        // Dot width * number of dots to scroll past * space between those dots
        const scrollDistance = 12 * startIndex;
        if (containerRef && containerRef.current) {
            containerRef.current.scrollLeft = scrollDistance;
        }
    }, [containerRef, startIndex]);
    const handleClick = (e) => {
        const clickedIndex = Number(e.currentTarget?.value);
        calculateStartIndex(clickedIndex);
        changeIndex && changeIndex(clickedIndex);
    };
    const renderDots = () => {
        const paginationDots = [];
        for (let index = 0; index < itemCount; index++) {
            const isFirstDot = index === startIndex;
            const isLastDot = index === startIndex + maxDots - 1;
            const shouldFocus = activeIndex === index;
            // Truncate: if first or last pagination dot AND there are more pages in that direction
            const shouldTruncate = (isFirstDot && !isAtBeginning()) || (isLastDot && !isAtEnd());
            const dotBaseClassName = `${baseClassName}-icon`;
            const dotClassNames = getClassNameList(dotBaseClassName, {
                [`${dotBaseClassName}-focus`]: shouldFocus,
                [`${dotBaseClassName}-truncate`]: shouldTruncate,
                [`${dotBaseClassName}-default`]: !shouldTruncate && !shouldFocus,
                [`${dotBaseClassName}-inset`]: inset,
                [`${dotBaseClassName}-offset`]: !inset,
            });
            const isOutOfView = index < startIndex || index >= startIndex + maxDots;
            paginationDots.push(jsx("button", { "aria-label": formatText('carousel.indexAndTotal', index + 1, itemCount), "aria-hidden": isOutOfView, "aria-current": index === activeIndex ? 'location' : false, tabIndex: isOutOfView ? -1 : 0, type: "button", className: getClassNameList(`${baseClassName}-button`), onClick: (e) => handleClick(e), value: index, children: jsx("div", { className: getClassNameList(dotClassNames), "aria-hidden": "true" }) }, index));
        }
        return paginationDots;
    };
    return (jsx("div", { ref: containerRef, className: getClassNameList(`${baseClassName}-container`), children: renderDots() }));
};
const EGDSCarouselPaginationDots = (props) => {
    const l10n = useLocalization();
    return jsx(CarouselPaginationDots, { l10n: l10n, ...props });
};
EGDSCarouselPaginationDots.displayName = 'EGDSCarouselPaginationDots';

export { Carousel as C, EGDSCarousel as E, EGDSCarouselBreakPointSizes as a, EGDSCarouselItem as b, EGDSCarouselItemList as c, EGDSCarouselLocalAnalyticsIdType as d, EGDSCarouselNav as e, EGDSCarouselPaginationDots as f, CarouselPaginationDots as g, EGDSHeadingTags as h };
//# sourceMappingURL=index-chunk.esm.js.map
